import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return sessionStorage.getItem('admin_auth') === 'true';
  });
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const handleLogin = (e) => {
    e.preventDefault();
    if (password.toLowerCase() === 'enercon') {
      sessionStorage.setItem('admin_auth', 'true');
      setIsAuthenticated(true);
      setPasswordError('');
    } else {
      setPasswordError('Incorrect password. Please try again.');
      setPassword('');
    }
  };

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/access-stats`);
      if (!response.ok) throw new Error('Failed to fetch stats');
      const data = await response.json();
      setStats(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchStats();
    }
  }, [isAuthenticated]);

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f0f9f4' }}>
        <Card className="w-full max-w-md shadow-2xl border-emerald-300">
          <CardHeader className="bg-gradient-to-r from-emerald-50 to-green-50 border-b border-emerald-200">
            <CardTitle className="text-2xl text-center" style={{ color: '#1B5E20', fontFamily: 'Montserrat, sans-serif' }}>
              Admin Access
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-8 pb-8 bg-white">
            <form onSubmit={handleLogin}>
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <p className="text-sm text-gray-600" style={{ fontFamily: 'Lato, sans-serif' }}>
                    🔒 Administrator access required
                  </p>
                  <p className="text-sm text-gray-600 mt-2" style={{ fontFamily: 'Lato, sans-serif' }}>
                    Please enter the admin password
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: 'Lato, sans-serif' }}>
                    Password
                  </label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter admin password"
                    className="w-full text-base"
                    autoFocus
                  />
                  {passwordError && (
                    <p className="text-red-600 text-sm mt-2" style={{ fontFamily: 'Lato, sans-serif' }}>
                      {passwordError}
                    </p>
                  )}
                </div>
                
                <Button 
                  type="submit"
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3"
                >
                  Access Admin
                </Button>
                
                <div className="text-center mt-6">
                  <Button 
                    onClick={() => window.location.href = '/'}
                    variant="outline"
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  >
                    Back to Dashboard
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f0f9f4' }}>
        <p className="text-xl" style={{ fontFamily: 'Lato, sans-serif' }}>Loading statistics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f0f9f4' }}>
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <p className="text-red-600">Error: {error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8" style={{ backgroundColor: '#f0f9f4' }}>
      <div className="max-w-6xl mx-auto">
        <Card className="shadow-lg border-emerald-200 mb-8">
          <CardHeader className="bg-gradient-to-r from-emerald-50 to-green-50 border-b border-emerald-100">
            <div className="flex items-center justify-between">
              <CardTitle className="text-2xl" style={{ color: '#2C3E50', fontFamily: 'Montserrat, sans-serif' }}>
                Dashboard Access Statistics
              </CardTitle>
              <div>
                <Button 
                  onClick={fetchStats}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white mr-4"
                >
                  Refresh
                </Button>
                <Button 
                  onClick={() => window.location.href = '/'}
                  variant="outline"
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                >
                  Back to Dashboard
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6 bg-white">
            <div className="mb-8">
              <h3 className="text-xl font-bold mb-4" style={{ color: '#2C3E50', fontFamily: 'Montserrat, sans-serif' }}>
                Summary
              </h3>
              <p className="text-3xl font-bold text-emerald-600">
                {stats?.total_accesses || 0} Total Accesses
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-xl font-bold mb-4" style={{ color: '#2C3E50', fontFamily: 'Montserrat, sans-serif' }}>
                Access by Code
              </h3>
              <div className="space-y-6">
                {stats?.by_code?.map((item) => (
                  <Card key={item.code} className="border-emerald-200">
                    <CardHeader className="bg-emerald-50">
                      <CardTitle className="text-xl text-emerald-700 uppercase flex items-center justify-between" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        <span>{item.code}</span>
                        <span className="text-3xl font-bold" style={{ color: '#2C3E50' }}>{item.count}</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Avg Duration</p>
                          <p className="text-lg font-bold text-emerald-600">
                            {Math.floor(item.avg_duration / 60)}m {item.avg_duration % 60}s
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Unique IPs</p>
                          <p className="text-lg font-bold text-emerald-600">{item.unique_ip_count || 0}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">First Access</p>
                          <p className="text-sm font-medium">{new Date(item.first_access).toLocaleDateString()}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Last Access</p>
                          <p className="text-sm font-medium">{new Date(item.last_access).toLocaleString()}</p>
                        </div>
                      </div>
                      
                      {/* Top Browsers */}
                      {item.browsers && Object.keys(item.browsers).length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs text-gray-500 mb-1">Browsers</p>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(item.browsers).map(([browser, count]) => (
                              <span key={browser} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                                {browser}: {count}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Top Devices */}
                      {item.devices && Object.keys(item.devices).length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs text-gray-500 mb-1">Devices</p>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(item.devices).map(([device, count]) => (
                              <span key={device} className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded">
                                {device}: {count}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Recent Sessions */}
                      {item.sessions && item.sessions.length > 0 && (
                        <details className="mt-4">
                          <summary className="cursor-pointer text-sm font-semibold text-emerald-700 hover:text-emerald-800">
                            View Session Details ({item.sessions.length} sessions)
                          </summary>
                          <div className="mt-3 space-y-2 max-h-96 overflow-y-auto">
                            {item.sessions.slice(0, 10).map((session, idx) => (
                              <div key={idx} className="p-3 bg-gray-50 rounded text-xs">
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <span className="font-semibold">Time:</span> {new Date(session.timestamp).toLocaleString()}
                                  </div>
                                  <div>
                                    <span className="font-semibold">Duration:</span> {Math.floor(session.duration / 60)}m {session.duration % 60}s
                                  </div>
                                  <div>
                                    <span className="font-semibold">IP:</span> {session.ip || 'Unknown'}
                                  </div>
                                  <div>
                                    <span className="font-semibold">Browser:</span> {session.browser || 'Unknown'}
                                  </div>
                                  <div>
                                    <span className="font-semibold">Device:</span> {session.device || 'Unknown'}
                                  </div>
                                  <div>
                                    <span className="font-semibold">OS:</span> {session.os || 'Unknown'}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold mb-4" style={{ color: '#2C3E50', fontFamily: 'Montserrat, sans-serif' }}>
                Recent Access Log (Last 20)
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-emerald-50">
                      <th className="p-3 text-left font-semibold" style={{ color: '#2C3E50' }}>Code</th>
                      <th className="p-3 text-left font-semibold" style={{ color: '#2C3E50' }}>Timestamp</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats?.recent_logs?.map((log, index) => (
                      <tr key={index} className="border-b border-gray-200 hover:bg-emerald-50">
                        <td className="p-3 font-bold text-emerald-700 uppercase">{log.code}</td>
                        <td className="p-3 text-gray-700">{new Date(log.timestamp).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Admin;
